import { createUseStyles } from "react-jss";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

/**
 * ToolToggle — the Pan / Select tool switch (WP-L).
 *
 * Rendered in the bottom status bar (EditorAlerts) so it does not overlap the
 * canvas pan-down arrow. Controlled component: the active `tool` ('pan' |
 * 'select') and the `onChange` setter are owned by NodeEditor, which also feeds
 * `tool` to TourCanvas so the canvas knows whether a left-drag pans the board
 * or draws a marquee.
 */

export const TOOLS = {
  PAN: "pan",
  SELECT: "select",
};

const BUTTONS = [
  { tool: TOOLS.PAN, icon: "hand", label: "Pan tool (drag to move the board)" },
  {
    tool: TOOLS.SELECT,
    icon: "arrow-pointer",
    label: "Select tool (drag to marquee-select nodes)",
  },
];

const ToolToggle = ({ tool = TOOLS.PAN, onChange }) => {
  const classes = useStyles();

  const select = next => {
    if (next !== tool && typeof onChange === "function") onChange(next);
  };

  return (
    <div className={classes.bar} role="group" aria-label="Canvas tool">
      {BUTTONS.map(({ tool: value, icon, label }) => (
        <button
          key={value}
          type="button"
          className={clsx(classes.button, {
            [classes.active]: tool === value,
          })}
          onClick={() => select(value)}
          aria-pressed={tool === value}
          aria-label={label}
          title={label}
        >
          <FontAwesomeIcon icon={["fal", icon]} className={classes.icon} />
        </button>
      ))}
    </div>
  );
};

const useStyles = createUseStyles(theme => ({
  bar: {
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
    gap: 2,
    padding: 2,
    borderRadius: theme.borderRadius ?? 6,
    border: `1px solid ${theme.colorDarkGrey}`,
    backgroundColor: theme.colorBackground,
  },
  button: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 32,
    padding: 0,
    border: "none",
    borderRadius: theme.borderRadius ?? 6,
    backgroundColor: "transparent",
    color: theme.colorLightGrey ?? "#ccc",
    cursor: "pointer",
    transitionProperty: "background-color, color",
    transitionDuration: theme.transitionDuration ?? "0.2s",
    "&:hover": {
      backgroundColor: "#313131",
      color: theme.colorWhite ?? "#fff",
    },
  },
  active: {
    backgroundColor: theme.colorPrimary,
    color: theme.colorWhite ?? "#fff",
    "&:hover": {
      backgroundColor: theme.colorPrimary,
    },
  },
  icon: {
    fontSize: 16,
  },
}));

export default ToolToggle;
